/**
 * Chat transcript display component
 * Shows conversation history with markdown support, inline tool calls,
 * and streaming indicators.
 */
import React, { useRef, useEffect, useState, useCallback, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, CircleStop } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../../lib/utils';
import { ThinkingDots } from './ThinkingDots';
import { ToolCallBlock } from './ToolCallBlock';
import { ApprovalActionBlock } from './ApprovalActionBlock';
import { EntityChip } from '../common/EntityChip';
import { TagChipStack, useEntityTagsBatch, entityTagsKey } from '../tags';
import { TAGGABLE_ENTITIES } from '../../../generated/taggable-entities';
import type { ResolvedEntity } from '../../types';
import { ChatMarkdown, type BlockSelection } from './ChatMarkdown';
import { countInteractiveBlocks, getInteractiveBlockAt, type InteractiveBlock } from './interactiveBlocks';
import { ToolCallDetailDialog } from './ToolCallDetailDialog';
import { CompactionMarker } from './CompactionMarker';
import { MessageAttachments } from './MessageAttachments';
import type { ChatMessage, StreamingState, ToolCallData } from './types';

/** Strip <thinking> tags from streaming text so they don't flash on screen. */
function stripThinkingTags(text: string): string {
  // Remove closed thinking blocks
  let clean = text.replace(/<thinking>[\s\S]*?<\/thinking>/g, '');
  // Remove unclosed thinking tag (still streaming inside a thinking block)
  clean = clean.replace(/<thinking>[\s\S]*$/g, '');
  // Remove partial opening tag being streamed char-by-char (e.g. "<thinki", "<thinking")
  clean = clean.replace(/<t(?:h(?:i(?:n(?:k(?:i(?:n(?:g)?)?)?)?)?)?)?$/g, '');
  // Remove [TOOL_CALLS] markers (internal history encoding that shouldn't render)
  clean = clean.replace(/\[TOOL_CALLS\][\s\S]*/g, '');
  return clean.trim();
}

/**
 * Streaming content - renders markdown with a blinking cursor.
 */
/** Strip completed and partial interactive blocks (```question / ```decision) from streaming text */
function stripInteractiveBlocks(text: string): string {
  // Strip completed blocks
  let clean = text.replace(/^```(?:question|decision)\s*\n[\s\S]*?^```/gm, '');
  // Strip partial (unclosed) block at end of stream
  clean = clean.replace(/```(?:question|decision)\s*\n[\s\S]*$/, '');
  return clean.trim();
}

/** Detect whether a message contains markdown formatting worth rendering */
const MARKDOWN_PATTERN = /(?:^|\n)#{1,6}\s|(?:^|\n)[-*]\s|\*\*[^*]+\*\*|__[^_]+__|```|^\d+\.\s/m;
function hasMarkdownSyntax(text: string): boolean {
  return MARKDOWN_PATTERN.test(text);
}

const StreamingContent: React.FC<{ text: string }> = ({ text }) => {
  const cleanText = stripInteractiveBlocks(stripThinkingTags(text));
  if (!cleanText) return null;
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanText}</ReactMarkdown>
      <span className="inline-block w-0.5 h-4 bg-primary animate-pulse align-text-bottom ms-1" />
    </div>
  );
};

/** Data for a pending interactive block to be rendered in the input area */
export interface PendingInteractiveBlock {
  block: InteractiveBlock;
  blockIndex: number;
  onSelect: (text: string, optionIndex: number) => void;
}

interface ChatTranscriptProps {
  /** Conversation messages */
  messages: ChatMessage[];
  /** Streaming state for real-time updates */
  streaming?: StreamingState;
  /** Height of the transcript container */
  height?: number | string;
  /** Empty state message */
  emptyMessage?: string;
  /** Custom empty state content (takes priority over emptyMessage) */
  emptyStateContent?: React.ReactNode;
  /** Session ID (for tool call detail fetching) */
  sessionId?: string;
  /** Called when user selects an interactive block option (auto-sends as message) */
  onSend?: (text: string) => void;
  /** Called when the current pending block changes (for rendering in input area) */
  onPendingBlockUpdate?: (pending: PendingInteractiveBlock | null) => void;
  /** Called when user clicks a created entity link (e.g., to close a panel) */
  onNavigate?: () => void;
  /** Optional: collapse the inline pending banner immediately when user clicks
   *  approve/reject/always so it doesn't flash back while the backend executes. */
  onApprovalResolveStart?: (toolCallId: string, action: 'approve' | 'reject' | 'always_approve') => void;
}

export const ChatTranscript: React.FC<ChatTranscriptProps> = ({
  messages,
  streaming = {},
  height = 300,
  emptyMessage,
  emptyStateContent,
  sessionId,
  onSend,
  onPendingBlockUpdate,
  onNavigate,
  onApprovalResolveStart,
}) => {
  const { t } = useTranslation('common');
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    response: currentResponse,
    transcript: currentTranscript,
    toolStatus,
    toolEvents = [],
    isGenerating,
    reasoning: currentReasoning,
  } = streaming;

  // Compute whether the streaming response has visible text after stripping thinking/interactive blocks
  const visibleStreamingText = useMemo(() => {
    if (!currentResponse) return '';
    return stripInteractiveBlocks(stripThinkingTags(currentResponse));
  }, [currentResponse]);

  // Track which interactive blocks have been answered (messageId → blockIndex → selection)
  const [answeredBlocks, setAnsweredBlocks] = useState<
    Map<string, Map<number, BlockSelection>>
  >(new Map());

  // Ref to hold the combined message ready to send (avoids side effects in state updater)
  const pendingSendRef = useRef<string | null>(null);

  // Lifted dialog state — survives message re-renders from query invalidation
  const [openToolCallId, setOpenToolCallId] = useState<string | null>(null);

  const handleOpenDetail = useCallback((id: string) => {
    setOpenToolCallId(id);
  }, []);

  // Find the tool call data for the currently-open dialog
  const openToolCallData: ToolCallData | undefined = useMemo(() => {
    if (!openToolCallId) return undefined;
    // Search persisted messages
    for (const msg of messages) {
      if (msg.tool_calls) {
        const found = msg.tool_calls.find(tc => tc.id === openToolCallId);
        if (found) return found;
      }
    }
    // Search streaming tool events
    const event = toolEvents.find(te => te.toolCallId === openToolCallId);
    if (event) {
      return {
        id: event.toolCallId || openToolCallId,
        tool_name: event.toolName,
        module: event.module,
        status: event.status as 'running' | 'success' | 'error',
        latency_ms: event.latencyMs ?? null,
        error_message: event.errorMessage ?? null,
        call_order: 0,
      };
    }
    return undefined;
  }, [openToolCallId, messages, toolEvents]);

  // Find the last assistant message index (only its blocks should be interactive)
  const lastAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return i;
    }
    return -1;
  }, [messages]);

  // The last assistant message is interactive only if no user message follows it
  const lastAssistantIsInteractive = useMemo(() => {
    if (lastAssistantIndex === -1) return false;
    // Check if there's a user message after the last assistant message
    for (let i = lastAssistantIndex + 1; i < messages.length; i++) {
      if (messages[i].role === 'user') return false;
    }
    return !isGenerating;
  }, [lastAssistantIndex, messages, isGenerating]);

  // Compute whether there are pending interactive blocks
  const hasPendingBlocks = useMemo(() => {
    if (!lastAssistantIsInteractive || lastAssistantIndex === -1) return false;
    const lastMsg = messages[lastAssistantIndex];
    if (!lastMsg) return false;
    const total = countInteractiveBlocks(lastMsg.content);
    if (total === 0) return false;
    const answered = lastMsg.id ? (answeredBlocks.get(lastMsg.id)?.size ?? 0) : 0;
    return answered < total;
  }, [lastAssistantIsInteractive, lastAssistantIndex, messages, answeredBlocks]);

  const handleInteractiveSelect = useCallback(
    (messageId: string, messageContent: string, blockIndex: number, optionIndex: number, text: string) => {
      setAnsweredBlocks((prev) => {
        const next = new Map(prev);
        const msgBlocks = new Map(next.get(messageId) || new Map());
        msgBlocks.set(blockIndex, { selectedIndex: optionIndex, selectedText: text });
        next.set(messageId, msgBlocks);

        // Check if all blocks in this message are now answered
        const totalBlocks = countInteractiveBlocks(messageContent);
        if (msgBlocks.size >= totalBlocks) {
          // Collect answers in block order and stage for sending
          const answers: string[] = [];
          for (let i = 0; i < totalBlocks; i++) {
            const selection = msgBlocks.get(i);
            if (selection) answers.push(selection.selectedText);
          }
          pendingSendRef.current = answers.join('\n');
        }

        return next;
      });
    },
    [],
  );

  // Send pending message after state update completes (no side effects in state updater)
  useEffect(() => {
    if (pendingSendRef.current !== null) {
      const message = pendingSendRef.current;
      pendingSendRef.current = null;
      onSend?.(message);
    }
  }, [answeredBlocks, onSend]);

  // Compute and provide the current pending block for bottom-area rendering
  const pendingBlockSelect = useCallback(
    (text: string, optionIndex: number) => {
      if (lastAssistantIndex === -1) return;
      const msg = messages[lastAssistantIndex];
      if (!msg?.id) return;
      const answered = answeredBlocks.get(msg.id)?.size ?? 0;
      handleInteractiveSelect(msg.id, msg.content, answered, optionIndex, text);
    },
    [lastAssistantIndex, messages, answeredBlocks, handleInteractiveSelect],
  );

  useEffect(() => {
    if (!hasPendingBlocks || lastAssistantIndex === -1) {
      onPendingBlockUpdate?.(null);
      return;
    }
    const msg = messages[lastAssistantIndex];
    if (!msg) { onPendingBlockUpdate?.(null); return; }
    const answeredCount = msg.id ? (answeredBlocks.get(msg.id)?.size ?? 0) : 0;
    const block = getInteractiveBlockAt(msg.content, answeredCount);
    if (!block) { onPendingBlockUpdate?.(null); return; }
    onPendingBlockUpdate?.({
      block,
      blockIndex: answeredCount,
      onSelect: pendingBlockSelect,
    });
  }, [hasPendingBlocks, lastAssistantIndex, messages, answeredBlocks, onPendingBlockUpdate, pendingBlockSelect]);

  // Auto-scroll to bottom when new content arrives.
  // Double rAF waits for layout (markdown, images, attachments) to settle
  // so scrollHeight reflects the final content height. Without this,
  // hydrating a full transcript on mount scrolls before content has height.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let rafB: number | undefined;
    const rafA = requestAnimationFrame(() => {
      rafB = requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    });
    return () => {
      cancelAnimationFrame(rafA);
      if (rafB !== undefined) cancelAnimationFrame(rafB);
    };
  }, [messages, currentTranscript, currentResponse, toolStatus, toolEvents, answeredBlocks]);

  const isEmpty = messages.length === 0 && !currentTranscript && !currentResponse;

  return (
    <div
      ref={scrollRef}
      className={`overflow-auto px-2 py-1 sm:px-4 sm:py-2${height === '100%' ? ' h-full' : ''}`}
      style={height === '100%' ? undefined : { height }}
    >
      {isEmpty && (
        emptyStateContent || (
          <p className="text-muted-foreground text-sm text-center py-4">
            {emptyMessage || t('avatar.typeToStart')}
          </p>
        )
      )}

      {!isEmpty && (
        <div className="flex flex-col gap-3">
          {/* Completed messages */}
          {messages.map((message, index) => {
            // Find the next user message after this assistant message (for reconstructing selections)
            let userReply: string | undefined;
            if (message.role === 'assistant') {
              for (let j = index + 1; j < messages.length; j++) {
                if (messages[j].role === 'user') {
                  userReply = messages[j].content;
                  break;
                }
              }
            }
            return (
            <MessageBubble
              key={message.id || index}
              message={message}
              interactive={
                message.role === 'assistant' &&
                index === lastAssistantIndex &&
                lastAssistantIsInteractive
              }
              answeredBlocks={
                message.id ? answeredBlocks.get(message.id) : undefined
              }
              userReply={userReply}
              onOpenToolDetail={handleOpenDetail}
              onNavigate={onNavigate}
              onApprovalResolveStart={onApprovalResolveStart}
            />
            );
          })}

          {/* Current user transcript (speech being recognized) */}
          {currentTranscript && (
            <div className="flex justify-end">
              <div className="rounded-2xl max-w-[85%] sm:max-w-[70%] bg-foreground/5 px-4 py-2.5 opacity-70">
                <p className="text-sm italic">{currentTranscript}...</p>
              </div>
            </div>
          )}

          {/* Thinking indicator — shown while generating and no visible response yet */}
          {isGenerating && !visibleStreamingText && (
            <div className="py-2">
              <ThinkingDots
                toolEvents={toolEvents}
                toolStatus={toolStatus}
                reasoning={currentReasoning}
              />
            </div>
          )}

          {/* Current assistant response (streaming) — only when there's visible text */}
          {visibleStreamingText && (
            <div className="min-w-0">
              <StreamingContent text={currentResponse!} />
              {/* Inline tool activity indicator — shown when tools run mid-stream */}
              {isGenerating && toolEvents.length > 0 && (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  {toolEvents.map((event, i) => (
                    <ToolCallBlock
                      key={`${event.toolName}-${i}`}
                      toolEvent={event}
                      onOpenDetail={handleOpenDetail}
                      onResolveStart={onApprovalResolveStart}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Single detail dialog — state lifted here so it survives message re-renders */}
      <ToolCallDetailDialog
        open={!!openToolCallId}
        onClose={() => setOpenToolCallId(null)}
        toolCall={openToolCallData}
        toolCallId={openToolCallId || undefined}
        sessionId={sessionId}
      />
    </div>
  );
};


/** Extract <thinking> blocks from assistant message content.
 * Handles both closed (<thinking>...</thinking>) and unclosed (<thinking>... to end) tags. */
function extractThinking(content: string): { thinkingText: string; cleanContent: string } {
  const blocks: string[] = [];
  // First pass: extract closed thinking blocks
  let clean = content.replace(/<thinking>([\s\S]*?)<\/thinking>/g, (_, text: string) => {
    blocks.push(text.trim());
    return '';
  });
  // Second pass: extract unclosed thinking tag (runs to end of content)
  clean = clean.replace(/<thinking>([\s\S]*)$/g, (_, text: string) => {
    if (text.trim()) blocks.push(text.trim());
    return '';
  });
  // Strip [TOOL_CALLS] markers (internal history encoding that shouldn't render)
  clean = clean.replace(/\[TOOL_CALLS\][\s\S]*/g, '');
  return { thinkingText: blocks.join('\n'), cleanContent: clean.trim() };
}

/** Max chips to show in one row before collapsing */
const CHIP_ROW_LIMIT = 6;

/** Entity-reference table name → its taggable `{module}.{entity}` type. */
const TAGGABLE_TYPE_BY_TABLE = new Map(
  TAGGABLE_ENTITIES.map((info) => [info.table, info.entityType]),
);

/** Collapsible entity chip list — shows one row with expand toggle.
 *  Each taggable entity reference also renders its tags as clickable chips. */
const EntityChipList: FC<{ chips: ResolvedEntity[] }> = ({ chips }) => {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(false);
  const needsCollapse = chips.length > CHIP_ROW_LIMIT;
  const visible = useMemo(
    () =>
      needsCollapse && !expanded ? chips.slice(0, CHIP_ROW_LIMIT) : chips,
    [chips, needsCollapse, expanded],
  );

  // Resolve tags for every taggable entity referenced, in one batched request.
  const taggableRefs = useMemo(
    () =>
      visible
        .map((e) => {
          const entityType = TAGGABLE_TYPE_BY_TABLE.get(e.entity_type);
          return entityType ? { entityType, entityId: e.entity_id } : null;
        })
        .filter((r): r is { entityType: string; entityId: string } => r !== null),
    [visible],
  );
  const tagsByEntity = useEntityTagsBatch(taggableRefs);

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1.5 items-center">
        {visible.map((entity) => {
          const taggableType = TAGGABLE_TYPE_BY_TABLE.get(entity.entity_type);
          const tags = taggableType
            ? tagsByEntity.get(entityTagsKey(taggableType, entity.entity_id))
            : undefined;
          return (
            <span
              key={entity.entity_ref}
              className="inline-flex items-center gap-1"
            >
              <EntityChip entity={entity} size="sm" openInNewTab />
              {tags && taggableType && (
                <TagChipStack
                  tags={tags}
                  size="sm"
                  max={3}
                  entityType={taggableType}
                />
              )}
            </span>
          );
        })}
        {needsCollapse && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            {expanded
              ? t('app.showLess')
              : t('app.nMore', { count: chips.length - CHIP_ROW_LIMIT })}
            <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
          </button>
        )}
      </div>
    </div>
  );
};

/** Single message bubble with optional interactive blocks and tool call history */
const MessageBubble: FC<{
  message: ChatMessage;
  interactive?: boolean;
  answeredBlocks?: Map<number, BlockSelection>;
  /** The next user message content (for reconstructing selections on past blocks) */
  userReply?: string;
  onOpenToolDetail?: (id: string) => void;
  onNavigate?: () => void;
  onApprovalResolveStart?: (toolCallId: string, action: 'approve' | 'reject' | 'always_approve') => void;
}> = ({ message, interactive = false, answeredBlocks, userReply, onOpenToolDetail, onNavigate, onApprovalResolveStart }) => {
  const { t } = useTranslation('aiChat');
  const toolCalls = message.tool_calls;
  const hasToolCalls = message.role === 'assistant' && toolCalls && toolCalls.length > 0;

  // Split tool calls: approval-related ones render outside the collapsible
  const { approvalTools, regularTools } = useMemo(() => {
    if (!hasToolCalls) return { approvalTools: [] as ToolCallData[], regularTools: [] as ToolCallData[] };
    const approval: ToolCallData[] = [];
    const regular: ToolCallData[] = [];
    for (const tc of toolCalls!) {
      if (tc.approval_id || tc.status === 'pending_approval') {
        approval.push(tc);
      } else {
        regular.push(tc);
      }
    }
    return { approvalTools: approval, regularTools: regular };
  }, [hasToolCalls, toolCalls]);

  const hasRegularTools = regularTools.length > 0;
  const [thinkingExpanded, setThinkingExpanded] = useState(false);

  const { thinkingText, cleanContent } = useMemo(() => {
    if (message.role !== 'assistant') return { thinkingText: '', cleanContent: message.content };
    return extractThinking(message.content);
  }, [message.content, message.role]);

  // Collect unique entities from tool calls for display as shared EntityChips
  const entityChips = useMemo((): ResolvedEntity[] => {
    if (!hasToolCalls) return [];
    const seen = new Set<string>();
    const entities: ResolvedEntity[] = [];
    for (const tc of toolCalls!) {
      if (tc.status !== 'success' || !tc.entities) continue;
      for (const entity of tc.entities) {
        if (!entity.title || seen.has(entity.ref)) continue;
        seen.add(entity.ref);
        const [entityType, entityId] = entity.ref.split(':');
        entities.push({
          entity_ref: entity.ref,
          entity_type: entityType,
          entity_id: entityId,
          title: entity.title,
          board_id: entity.context?.board_id,
          action: entity.action,
        });
      }
    }
    return entities;
  }, [hasToolCalls, toolCalls]);

  const hasThinkingContent = !!thinkingText || hasRegularTools;

  // Build the collapsible label (only counts regular tools, not approval tools)
  const thinkingLabel = useMemo(() => {
    if (thinkingText && hasRegularTools) {
      return t('thinkingWithTools', { count: regularTools.length });
    }
    if (thinkingText) return t('thinkingCollapsed');
    if (hasRegularTools) return t('usedTools', { count: regularTools.length });
    return '';
  }, [thinkingText, hasRegularTools, regularTools, t]);

  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  // Injected system notifications (e.g., approval updates) — show as info, hide tool_result
  const isInjectedSystem = isUser && message.content.startsWith('[System]');

  // Compaction summary messages render as an inline divider with optional
  // sysadmin-only popover showing the full summary content.
  if (isSystem && message.is_compaction_summary) {
    return <CompactionMarker content={message.content} />;
  }

  // System messages (errors) get a distinctive centered error style
  if (isSystem) {
    return (
      <div className="flex justify-center py-1">
        <div className="max-w-[85%] sm:max-w-[70%] bg-destructive/10 text-destructive rounded-lg px-4 py-2.5 text-center">
          <p className="text-sm">{message.content}</p>
        </div>
      </div>
    );
  }

  // Injected system notifications render as centered info messages
  if (isInjectedSystem) {
    // Strip [System] prefix and <tool_result>...</tool_result> block
    const cleanText = message.content
      .replace(/^\[System\]\s*/, '')
      .replace(/\n*<tool_result>[\s\S]*<\/tool_result>\s*$/, '')
      .trim();
    return (
      <div className="flex justify-center py-1">
        <div className="max-w-[85%] sm:max-w-[70%] bg-muted/50 text-muted-foreground rounded-lg px-4 py-2 text-center">
          <p className="text-xs">{cleanText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      isUser ? 'flex justify-end' : ''
    )}>
      <div className={cn(
        'min-w-0',
        isUser
          ? 'max-w-[85%] sm:max-w-[70%] bg-accent text-accent-foreground rounded-2xl px-4 py-2.5'
          : ''
      )}>
        {/* Collapsible thinking + tool call section */}
        {hasThinkingContent && (
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setThinkingExpanded(!thinkingExpanded)}
              className={cn(
                'flex items-center gap-1.5 text-xs transition-colors',
                'text-muted-foreground hover:text-foreground'
              )}
            >
              <ChevronRight className={cn(
                'h-3 w-3 transition-transform',
                thinkingExpanded && 'rotate-90',
              )} />
              <span>{thinkingLabel}</span>
            </button>
            {thinkingExpanded && (
              <div className="mt-1.5 space-y-1 pl-4">
                {thinkingText && (
                  <p className={cn(
                    'text-xs italic whitespace-pre-wrap text-muted-foreground'
                  )}>{thinkingText}</p>
                )}
                {regularTools.map((tc) => (
                  <ToolCallBlock
                    key={tc.id}
                    toolCall={tc}
                    onOpenDetail={onOpenToolDetail}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {message.role === 'assistant' ? (
          <ChatMarkdown
            content={cleanContent}
            interactive={interactive}
            answeredBlocks={answeredBlocks}
            userReply={userReply}
          />
        ) : hasMarkdownSyntax(message.content) ? (
          <ChatMarkdown content={message.content} />
        ) : (
          <p className="text-sm">{message.content}</p>
        )}

        {message.role === 'assistant' && message.interrupted && (
          <div className="mt-1.5 flex items-center gap-1.5 text-xs italic text-muted-foreground">
            <CircleStop className="h-3 w-3" />
            <span>{t('responseInterrupted')}</span>
          </div>
        )}

        {/* Approval action blocks — rendered prominently below message content */}
        {approvalTools.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {approvalTools.map((tc) => (
              <ApprovalActionBlock
                key={tc.id}
                toolCall={tc}
                onOpenDetail={onOpenToolDetail}
                onResolveStart={onApprovalResolveStart}
              />
            ))}
          </div>
        )}

        {/* Attachments on user messages (files sent with the prompt) */}
        {isUser && message.attachment_file_ids && message.attachment_file_ids.length > 0 && (
          <MessageAttachments fileIds={message.attachment_file_ids} align="end" />
        )}

        {/* Entity chips for referenced entities — collapsible when many */}
        {entityChips.length > 0 && (
          <EntityChipList chips={entityChips} />
        )}
      </div>
    </div>
  );
};
